"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

const DEV_PWA_STORAGE_KEY =
  "fornecedor_enable_pwa_dev";

export default function ServiceWorkerRegister() {
  const pathname = usePathname();
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return;
    }

    const isDevelopment =
      process.env.NODE_ENV !== "production" ||
      window.location.hostname === "dev.fornecedor.net" ||
      window.location.hostname === "admin-dev.fornecedor.net";

    // O PDV se prepara automaticamente em DEV e PROD, sem intervenção no navegador.
    // Não habilita cache de HTML privado: isso continua sob controle de sw.js.
    const isPosRoute = window.location.pathname === "/empresa/food/pdv" ||
      window.location.pathname.startsWith("/empresa/food/pdv/") ||
      window.location.pathname === "/empresa/food/caixa" || window.location.pathname === "/empresa/food/caixa/";

    // Uma visita ao PDV permite manter o SW registrado em DEV para a proxima queda.
    // Nao ativa cache HTML de outras paginas administrativas.
    const preparedKey = "fornecedor_pdv_pwa_prepared_dev";
    if (isPosRoute) {
      try { window.localStorage.setItem(preparedKey, "1"); } catch {}
    }
    let devPwaEnabled = isPosRoute;
    if (!devPwaEnabled) {
      try { devPwaEnabled = window.localStorage.getItem(preparedKey) === "1"; } catch {}
    }

    if (isDevelopment && !devPwaEnabled) {
      try {
        devPwaEnabled =
          window.localStorage.getItem(
            DEV_PWA_STORAGE_KEY,
          ) === "1";
      } catch {
        devPwaEnabled = false;
      }
    }

    /*
     * No DEV o comportamento padrão continua sendo:
     * - não registrar Service Worker
     * - remover registros antigos
     * - remover caches antigos
     *
     * Para testar PWA no DEV explicitamente, ativamos:
     *
     * localStorage.setItem(
     *   "fornecedor_enable_pwa_dev",
     *   "1",
     * )
     */
    if (isDevelopment && !devPwaEnabled) {
      async function cleanupDevelopmentServiceWorker() {
        try {
          const registrations =
            await navigator.serviceWorker.getRegistrations();

          await Promise.all(
            registrations.map((registration) =>
              registration.unregister(),
            ),
          );

          if ("caches" in window) {
            const cacheNames =
              await caches.keys();

            await Promise.all(
              cacheNames
                .filter((cacheName) =>
                  cacheName.startsWith("fornecedor-pwa-") ||
                  cacheName === "food-pos-shell-v1" || cacheName === "food-pos-public-shell-v2",
                )
                .map((cacheName) =>
                  caches.delete(cacheName),
                ),
            );
          }
        } catch (error) {
          console.warn(
            "Não foi possível limpar o Service Worker do ambiente DEV.",
            error,
          );
        }
      }

      void cleanupDevelopmentServiceWorker();
      return;
    }

    let isMounted = true;

    async function registerServiceWorker() {
      const report = (message: string, error = false) => {
        try {
          const detail = `${new Date().toLocaleTimeString("pt-BR")} — ${message}`;
          window.localStorage.setItem("fornecedor_pdv_sw_status", detail);

          if (error) {
            window.localStorage.setItem(
              "fornecedor_pdv_sw_last_error",
              detail,
            );
          } else {
            window.localStorage.removeItem(
              "fornecedor_pdv_sw_last_error",
            );
          }

          window.dispatchEvent(
            new Event("fornecedor-pdv-sw-diagnostic"),
          );
        } catch {}
      };

      report("Iniciando registro de /sw.js");

      try {
        const registration =
          await navigator.serviceWorker.register("/sw.js", {
            scope: "/",
            updateViaCache: "none",
          });

        if (!isMounted) return;

        report(
          `Registro concluído. Ativo: ${
            registration.active ? "SIM" : "NÃO"
          }; instalando: ${
            registration.installing ? "SIM" : "NÃO"
          }`,
        );
      } catch (error) {
        const detail =
          error instanceof Error
            ? `${error.name}: ${error.message}`
            : String(error);

        let httpDetail = "";

        try {
          const response = await fetch("/sw.js", {
            cache: "no-store",
          });

          httpDetail = ` | GET /sw.js: HTTP ${response.status}; tipo: ${
            response.headers.get("content-type") ?? "desconhecido"
          }`;
        } catch (fetchError) {
          httpDetail = ` | GET /sw.js falhou: ${String(fetchError)}`;
        }

        report(detail + httpDetail, true);
        console.warn(
          "Não foi possível registrar o aplicativo.",
          error,
        );
      }
    }

    // Preparação automática espera hidratação e ACK em FoodPosOfflineStatus.
    void registerServiceWorker();

    return () => {
      isMounted = false;
    };
  }, [pathname]);

  return null;
}
